import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Direction } from '@tank-battle/shared';
import { BattleRenderer } from './BattleRenderer.js';
import { useBattleSession } from './useBattleSession.js';
import { directionArrow } from './actionHelpers.js';
import { copyBattleInvite, shareBattleInvite } from './inviteShare.js';
import { getTelegramInitData } from '../config.js';

const ROTATE_DIRS: Direction[] = [0, 1, 2, 3];

export function BattleDebugApp() {
  const { t, i18n } = useTranslation();
  const {
    state,
    activeUnit,
    isMyTurn,
    actionMode,
    setActionMode,
    launchBattleId,
    startAsSideA,
    joinAsSideB,
    findOpponent,
    cancelMatchmaking,
    endTurn,
    moveTo,
    fireAt,
    rotateTo,
    canMoveTo,
    canFireAt,
  } = useBattleSession();
  const [joinId, setJoinId] = useState('');
  const [copied, setCopied] = useState(false);
  const [shareStatus, setShareStatus] = useState<string | null>(null);

  const initDataPresent = Boolean(getTelegramInitData());
  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user?.first_name;
  const inviteOptions = {
    inviterName: tgUser,
    locale: (i18n.language.startsWith('en') ? 'en' : 'ru') as 'ru' | 'en',
  };

  useEffect(() => {
    if (launchBattleId) {
      setJoinId(launchBattleId);
    }
  }, [launchBattleId]);

  useEffect(() => {
    if (launchBattleId && !state.battleId && state.phase === 'lobby') {
      void joinAsSideB(launchBattleId);
    }
  }, [joinAsSideB, launchBattleId, state.battleId, state.phase]);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    const mb = tg?.MainButton;
    if (!mb || state.phase !== 'waiting' || !state.battleId) {
      mb?.hide();
      return;
    }

    const locale = i18n.language.startsWith('en') ? 'en' : 'ru';
    const onShare = () => {
      const result = shareBattleInvite(state.battleId!, {
        inviterName: tgUser,
        locale,
      });
      if (result === 'shared') setShareStatus(t('battle.shareOpened'));
      else if (result === 'copied') setShareStatus(t('battle.copied'));
    };

    mb.text = t('battle.shareInviteContact');
    mb.show();
    mb.onClick(onShare);
    return () => {
      mb.offClick(onShare);
      mb.hide();
    };
  }, [state.phase, state.battleId, tgUser, i18n.language, t]);

  const handleCellClick = (x: number, y: number) => {
    if (actionMode === 'move') moveTo(x, y);
    if (actionMode === 'fire') fireAt(x, y);
  };

  const handleShareInvite = () => {
    if (!state.battleId) return;
    const result = shareBattleInvite(state.battleId, inviteOptions);
    if (result === 'shared') {
      setShareStatus(t('battle.shareOpened'));
    } else if (result === 'copied') {
      setCopied(true);
      setShareStatus(t('battle.copiedFallback'));
      window.setTimeout(() => setCopied(false), 2000);
    } else {
      setShareStatus(t('battle.shareUnsupported'));
    }
  };

  const handleCopyInvite = async () => {
    if (!state.battleId) return;
    const ok = await copyBattleInvite(state.battleId, inviteOptions);
    if (ok) {
      setCopied(true);
      setShareStatus(t('battle.copied'));
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  if (state.phase === 'matching') {
    return (
      <div className="debug-app">
        <header className="debug-header">
          <h1>{t('app.title')}</h1>
          <p>{t('battle.matchmakingTitle')}</p>
        </header>
        <div className="waiting-panel">
          <p className="waiting-title">{t('battle.searchingOpponent')}</p>
          <p className="hint">
            {state.queueSize > 0
              ? t('battle.queueSize', { n: state.queueSize })
              : t('battle.matchmakingHint')}
          </p>
          <div className="invite-actions">
            <button type="button" className="btn" onClick={() => void cancelMatchmaking()}>
              {t('battle.cancelSearch')}
            </button>
          </div>
          {state.error ? <p className="error">{state.error}</p> : null}
        </div>
      </div>
    );
  }

  if (state.phase === 'lobby') {
    return (
      <div className="debug-app">
        <header className="debug-header">
          <h1>{t('app.title')}</h1>
          <p>{tgUser ? t('battle.welcome', { name: tgUser }) : t('battle.debugSubtitle')}</p>
        </header>

        <div className="lobby-panel">
          {launchBattleId ? (
            <p className="hint">{t('battle.autoJoin', { id: launchBattleId.slice(0, 8) })}…</p>
          ) : (
            <p className="hint">{t('battle.lobbyHint')}</p>
          )}
          <p className="meta">
            {initDataPresent ? t('battle.initDataOk') : t('battle.initDataDev')}
          </p>

          <div className="lobby-actions">
            <button type="button" className="btn primary btn-large" onClick={() => void findOpponent()}>
              {t('battle.findOpponent')}
            </button>

            <p className="lobby-divider">{t('battle.orManual')}</p>

            <button type="button" className="btn" onClick={() => void startAsSideA()}>
              {t('battle.createSideA')}
            </button>

            {!launchBattleId ? (
              <div className="join-row">
                <input
                  value={joinId}
                  onChange={(e) => setJoinId(e.target.value)}
                  placeholder={t('battle.battleIdPlaceholder')}
                />
                <button
                  type="button"
                  className="btn"
                  disabled={!joinId.trim()}
                  onClick={() => void joinAsSideB(joinId)}
                >
                  {t('battle.joinSideB')}
                </button>
              </div>
            ) : null}
          </div>

          {state.error ? <p className="error">{state.error}</p> : null}
          {state.status ? <p className="status">{state.status}</p> : null}
        </div>
      </div>
    );
  }

  const waiting = state.phase === 'waiting';

  return (
    <div className="debug-app battle-screen">
      <header className="debug-header compact">
        <div>
          <strong>{t('battle.side', { side: state.side })}</strong>
          {activeUnit ? (
            <span className="hp"> HP {activeUnit.hp}/{activeUnit.maxHp}</span>
          ) : null}
        </div>
        <div className="header-stats">
          <span>{t('battle.turn', { n: state.view?.turn ?? 1 })}</span>
          <span className="timer">{t('battle.timer', { sec: state.secondsLeft })}</span>
          {activeUnit ? (
            <span>{t('battle.ap', { cur: activeUnit.ap, max: activeUnit.maxAp })}</span>
          ) : null}
        </div>
      </header>

      {state.map && state.view && !waiting ? (
        <div className="grid-wrap">
          <BattleRenderer
            map={state.map}
            view={state.view}
            mySide={state.side!}
            actionMode={actionMode}
            canInteract={isMyTurn && state.phase === 'battle'}
            onCellClick={handleCellClick}
            canMoveTo={canMoveTo}
            canFireAt={canFireAt}
            lastAction={state.lastAction ?? undefined}
          />
        </div>
      ) : (
        <div className="waiting-panel">
          <p className="waiting-title">{t('battle.waitOpponent')}</p>
          <p className="hint">{t('battle.shareContactHint')}</p>
          {state.battleId ? (
            <div className="invite-actions">
              <button type="button" className="btn primary btn-large" onClick={handleShareInvite}>
                {t('battle.shareInviteContact')}
              </button>
              <button type="button" className="btn" onClick={() => void handleCopyInvite()}>
                {copied ? t('battle.copied') : t('battle.copyLink')}
              </button>
              {shareStatus ? <p className="status">{shareStatus}</p> : null}
            </div>
          ) : null}
        </div>
      )}

      {!waiting ? (
        <>
          <div className="mode-bar">
            {(['move', 'fire', 'rotate_turret', 'rotate_hull'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                className={`btn mode-btn ${actionMode === mode ? 'active' : ''}`}
                disabled={!isMyTurn}
                onClick={() => setActionMode(mode)}
              >
                {t(`battle.mode.${mode}`)}
              </button>
            ))}
          </div>

          {(actionMode === 'rotate_turret' || actionMode === 'rotate_hull') && isMyTurn ? (
            <div className="rotate-bar">
              {ROTATE_DIRS.map((dir) => (
                <button
                  key={dir}
                  type="button"
                  className="btn"
                  onClick={() =>
                    rotateTo(
                      actionMode === 'rotate_turret' ? 'ROTATE_TURRET' : 'ROTATE_HULL',
                      dir,
                    )
                  }
                >
                  {directionArrow(dir)}
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      <footer className="battle-footer">
        <div className="footer-left">
          <span className={isMyTurn ? 'turn-active' : 'turn-wait'}>
            {waiting
              ? t('battle.waitOpponent')
              : isMyTurn
                ? t('battle.yourTurn')
                : t('battle.waitTurn')}
          </span>
          {state.status && !waiting ? (
            <span className="status-inline">{state.status}</span>
          ) : null}
        </div>
        {!waiting ? (
          <button
            type="button"
            className="btn primary"
            disabled={!isMyTurn || !state.view?.activeUnitId || state.phase !== 'battle'}
            onClick={endTurn}
          >
            {t('battle.endTurn')}
          </button>
        ) : null}
      </footer>

      {state.error ? <p className="error floating">{state.error}</p> : null}
      {state.phase === 'ended' && state.winner ? (
        <div className="overlay">
          <p>
            {state.winner === state.side
              ? t('battle.youWin')
              : t('battle.youLose', { side: state.winner })}
          </p>
        </div>
      ) : null}
    </div>
  );
}
